// Work Orders — the Design -> field-dispatch board.
// URL: /work-orders
// Generates work orders from a project's committed schedule (one WO per
// schedule phase, grouping that phase's tasks), then lets you assign a
// field crew and advance status. Backed by /api/work-orders.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

type Project = {
  id: string;
  project_code: string;
  project_name: string;
  state?: string;
  prime_contractor?: string;
};

type Crew = {
  id: string;
  name: string;
  type: string;
  color: string;
  is_active: boolean;
};

type WorkOrder = {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  type: string;
  priority: string;
  status: string;
  crew_id: string | null;
  crew_name: string | null;
  crew_type: string | null;
  crew_color: string | null;
  scheduled_for: string | null;
  completed_at: string | null;
  task_count: number;
  done_count: number;
  progress: number;
};

const TYPE_META: Record<string, { label: string; color: string; icon: string }> = {
  boring:      { label: 'Boring',      color: '#7c3aed', icon: '🛠' },
  trenching:   { label: 'Trenching',   color: '#2563eb', icon: '⛏' },
  aerial:      { label: 'Aerial',      color: '#0891b2', icon: '🗼' },
  splicing:    { label: 'Splicing',    color: '#059669', icon: '🔌' },
  locate:      { label: 'Locate',      color: '#d97706', icon: '📍' },
  restoration: { label: 'Restoration', color: '#65a30d', icon: '🌱' },
  survey:      { label: 'Survey',      color: '#dc2626', icon: '📐' },
  other:       { label: 'Other',       color: '#64748b', icon: '◆' },
};

const STATUS_META: Record<string, { label: string; bg: string; fg: string }> = {
  open:        { label: 'Open',        bg: '#e2e8f0', fg: '#334155' },
  scheduled:   { label: 'Scheduled',   bg: '#dbeafe', fg: '#1e40af' },
  in_progress: { label: 'In progress', bg: '#fef3c7', fg: '#92400e' },
  blocked:     { label: 'Blocked',     bg: '#fecaca', fg: '#7f1d1d' },
  complete:    { label: 'Complete',    bg: '#d1fae5', fg: '#065f46' },
  cancelled:   { label: 'Cancelled',   bg: '#f1f5f9', fg: '#64748b' },
};

const STATUS_ORDER = ['open', 'scheduled', 'in_progress', 'blocked', 'complete', 'cancelled'];

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_META[status] ?? STATUS_META.open;
  return (
    <span style={{
      background: s.bg, color: s.fg, padding: '2px 8px', borderRadius: 999,
      fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em',
      whiteSpace: 'nowrap',
    }}>{s.label}</span>
  );
}

export default function WorkOrdersPage() {
  const [projectId, setProjectId] = useState<string>('');
  const qc = useQueryClient();

  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ['bead-projects-workorders'],
    queryFn: () => api.get('/bead/projects'),
  });

  // Auto-select the first real project once the list loads.
  useEffect(() => {
    if (!projectId && projects.length > 0) setProjectId(projects[0].id);
  }, [projects, projectId]);

  const project = projects.find(p => p.id === projectId);

  const { data: crews = [] } = useQuery<Crew[]>({
    queryKey: ['work-order-crews'],
    queryFn: () => api.get('/work-orders/crews'),
  });

  const { data: workOrders = [], isLoading } = useQuery<WorkOrder[]>({
    queryKey: ['work-orders', projectId],
    queryFn: () => api.get(`/work-orders?project_id=${projectId}`),
    enabled: !!projectId,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['work-orders'] });

  const generateMut = useMutation({
    mutationFn: (regenerate: boolean) =>
      api.post('/work-orders/generate', { project_id: projectId, regenerate }),
    onSuccess: invalidate,
  });

  const assignMut = useMutation({
    mutationFn: ({ id, crew_id }: { id: string; crew_id: string | null }) =>
      api.post(`/work-orders/${id}/assign`, { crew_id }),
    onSuccess: invalidate,
  });

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.post(`/work-orders/${id}/status`, { status }),
    onSuccess: invalidate,
  });

  const kpis = useMemo(() => {
    const byStatus: Record<string, number> = {};
    let tasksTotal = 0, tasksDone = 0;
    for (const w of workOrders) {
      byStatus[w.status] = (byStatus[w.status] ?? 0) + 1;
      tasksTotal += w.task_count;
      tasksDone += w.done_count;
    }
    const active = (byStatus.scheduled ?? 0) + (byStatus.in_progress ?? 0);
    return {
      total: workOrders.length,
      open: byStatus.open ?? 0,
      active,
      complete: byStatus.complete ?? 0,
      tasksTotal,
      tasksDone,
    };
  }, [workOrders]);

  const alreadyGenerated = workOrders.length > 0;

  return (
    <>
      <div className="page__header">
        <div>
          <h1 className="page__title">Work Orders</h1>
          <div className="page__subtitle">
            Daily dispatch for all crews · {project ? `${project.project_code} · ${project.project_name}` : 'Loading…'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            value={projectId}
            onChange={e => setProjectId(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--border)', fontSize: 13, minWidth: 280 }}
          >
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.project_code} — {p.project_name}{p.state ? ` (${p.state})` : ''}</option>
            ))}
          </select>
          {alreadyGenerated && (
            <button
              onClick={() => { if (confirm('Regenerate work orders? This rebuilds them from the current schedule.')) generateMut.mutate(true); }}
              disabled={generateMut.isPending || !projectId}
              title="Rebuild work orders from the current schedule phases."
              style={{
                padding: '8px 14px', background: '#fff', color: '#0f172a',
                border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 12, fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {generateMut.isPending ? 'Working…' : '↻ Regenerate'}
            </button>
          )}
          <button
            onClick={() => generateMut.mutate(false)}
            disabled={generateMut.isPending || !projectId}
            style={{
              padding: '8px 16px',
              background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)',
              color: '#fff', border: 0, borderRadius: 4, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', opacity: (generateMut.isPending || !projectId) ? 0.6 : 1,
            }}
          >
            {generateMut.isPending ? 'Generating…' : '⚡ Generate work orders'}
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 16 }}>
        <Kpi label="Work orders" value={String(kpis.total)} accent="#0f172a" />
        <Kpi label="Open" value={String(kpis.open)} accent="#64748b" sub={kpis.open > 0 ? 'Needs a crew' : ''} />
        <Kpi label="Active" value={String(kpis.active)} accent="#f59e0b" sub="Scheduled or in progress" />
        <Kpi label="Complete" value={String(kpis.complete)} accent="#10b981" />
        <Kpi label="Tasks done" value={`${kpis.tasksDone}/${kpis.tasksTotal}`} accent="#3b82f6" sub="Across all work orders" />
      </div>

      {/* Board */}
      <div className="card">
        <div className="card__header">
          <span>Work orders · {workOrders.length}</span>
        </div>
        <div className="card__body" style={{ padding: 0 }}>
          {isLoading && (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
          )}
          {!isLoading && workOrders.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
              No work orders yet. Click <b>⚡ Generate work orders</b> to build them from this project's schedule phases.
            </div>
          )}
          {workOrders.map(w => (
            <WorkOrderRow
              key={w.id}
              wo={w}
              crews={crews}
              onAssign={(crew_id) => assignMut.mutate({ id: w.id, crew_id })}
              onStatus={(status) => statusMut.mutate({ id: w.id, status })}
              busy={
                (assignMut.isPending && assignMut.variables?.id === w.id) ||
                (statusMut.isPending && statusMut.variables?.id === w.id)
              }
            />
          ))}
        </div>
      </div>
    </>
  );
}

function WorkOrderRow({
  wo, crews, onAssign, onStatus, busy,
}: {
  wo: WorkOrder;
  crews: Crew[];
  onAssign: (crew_id: string | null) => void;
  onStatus: (status: string) => void;
  busy: boolean;
}) {
  const type = TYPE_META[wo.type] ?? TYPE_META.other;
  const pct = Math.max(0, Math.min(100, wo.progress || 0));
  return (
    <div
      style={{
        padding: '14px 16px', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 14,
        borderLeft: `4px solid ${wo.crew_color ?? type.color}`,
        opacity: busy ? 0.6 : 1,
      }}
    >
      <div style={{ fontSize: 20, width: 26, textAlign: 'center' }}>{type.icon}</div>

      {/* Title + meta */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>{wo.title}</span>
          <span style={{
            fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em',
            color: type.color, background: `${type.color}18`, padding: '1px 6px', borderRadius: 8,
          }}>{type.label}</span>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
          {wo.task_count} task{wo.task_count === 1 ? '' : 's'} · {wo.done_count} done
          {wo.scheduled_for && <> · starts {new Date(wo.scheduled_for).toLocaleDateString()}</>}
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ width: 120 }}>
        <div style={{ height: 6, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? '#10b981' : '#3b82f6' }} />
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3, textAlign: 'right' }}>{pct}%</div>
      </div>

      {/* Crew assign */}
      <select
        value={wo.crew_id ?? ''}
        disabled={busy}
        onChange={(e) => onAssign(e.target.value || null)}
        title="Assign a field crew"
        style={{
          padding: '6px 8px', borderRadius: 4, border: '1px solid var(--border)',
          fontSize: 12, minWidth: 160, background: '#fff',
        }}
      >
        <option value="">— Unassigned —</option>
        {crews.map(c => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>

      {/* Status control */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 190, justifyContent: 'flex-end' }}>
        <StatusBadge status={wo.status} />
        <select
          value={wo.status}
          disabled={busy}
          onChange={(e) => onStatus(e.target.value)}
          title="Advance status"
          style={{
            padding: '6px 8px', borderRadius: 4, border: '1px solid var(--border)',
            fontSize: 12, background: '#fff',
          }}
        >
          {STATUS_ORDER.map(s => (
            <option key={s} value={s}>{STATUS_META[s].label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div className="card" style={{ borderTop: accent ? `3px solid ${accent}` : undefined }}>
      <div className="card__body" style={{ padding: 14 }}>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 24, fontWeight: 800, marginTop: 4, color: '#0f172a' }}>{value}</div>
        {sub && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}
